import { useEffect, useState } from "react";

import { toast } from "sonner";

import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useApi } from "../../../context/ApiContext";
import { cn, getCharCount } from "../../../lib/utils.js";
import WordCount from "../../../components/shared/WordCount.jsx";

const VendorDialog = ({ isOpen, onOpenChange, onSuccess, editingVendor = null }) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vendorName: "",
    vendorType: "material_supplier",
    otherType: "",
    phone: "",
    email: "",
    address: "",
    paymentTerms: "net_30",
    status: "active"
  });
  const isAddressOverLimit = getCharCount(formData.address) > 500;

  useEffect(() => {
    if (editingVendor) {
      setFormData({
        vendorName: editingVendor.vendorName || "",
        vendorType: editingVendor.vendorType || "material_supplier",
        otherType: editingVendor.otherType || "",
        phone: editingVendor.phone || "",
        email: editingVendor.email || "",
        address: editingVendor.address || "",
        paymentTerms: editingVendor.paymentTerms || "net_30",
        status: editingVendor.status || "active"
      });
    } else {
      setFormData({
        vendorName: "",
        vendorType: "material_supplier",
        otherType: "",
        phone: "",
        email: "",
        address: "",
        paymentTerms: "net_30",
        status: "active"
      });
    }
  }, [editingVendor, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isAddressOverLimit) {
      toast.error("Address exceeds the 500-character limit.");
      return;
    }
    setLoading(true);
    try {
      let response;
      if (editingVendor) {
        response = await api.put(`/vendors/${editingVendor.id}`, formData);
        toast.success("Vendor updated successfully!");
      } else {
        response = await api.post(`/vendors`, formData);
        toast.success("Vendor created successfully!");
      }

      if (onSuccess) onSuccess(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Error saving vendor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{editingVendor ? "Edit Vendor" : "Create New Vendor"}</DialogTitle>
            <DialogDescription>
              {editingVendor ? "Update vendor information" : "Add a new vendor to the system"}
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendorName">
                  Vendor Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="vendorName"
                  value={formData.vendorName}
                  onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                  placeholder="Enter vendor name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="vendorType">
                  Vendor Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.vendorType}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      vendorType: value,
                      otherType: value === "other" ? formData.otherType : ""
                    })
                  }
                >
                  <SelectTrigger id="vendorType">
                    <SelectValue placeholder="Select vendor type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="material_supplier">Material Vendor</SelectItem>
                    <SelectItem value="equipment_rental">Equipment Rental</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.vendorType === "other" && (
              <div className="animate-in slide-in-from-top-2 duration-200">
                <Label htmlFor="otherType">
                  Other Type Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="otherType"
                  value={formData.otherType}
                  onChange={(e) => setFormData({ ...formData, otherType: e.target.value })}
                  placeholder="e.g., Transport Partner, Security, etc."
                  className="mt-1"
                  required={formData.vendorType === "other"}
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="paymentTerms">Payment Terms *</Label>
                <Select
                  value={formData.paymentTerms}
                  onValueChange={(value) => setFormData({ ...formData, paymentTerms: value })}
                >
                  <SelectTrigger id="paymentTerms">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net_5">Net 5</SelectItem>
                    <SelectItem value="net_10">Net 10</SelectItem>
                    <SelectItem value="net_15">Net 15</SelectItem>
                    <SelectItem value="net_20">Net 20</SelectItem>
                    <SelectItem value="net_30">Net 30</SelectItem>
                    <SelectItem value="net_60">Net 60</SelectItem>
                    <SelectItem value="net_90">Net 90</SelectItem>
                    <SelectItem value="cash_on_delivery">Cash on Delivery</SelectItem>
                    <SelectItem value="advance_payment">Advance Payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status *</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter address"
                rows={2}
                className={cn(isAddressOverLimit && "border-red-500")}
              />
              <WordCount text={formData.address} />
            </div>
          </div>
          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button type="submit" variant="primary" className="w-full py-6 rounded-lg shadow-lg" disabled={loading || isAddressOverLimit}>
              {loading ? "Saving..." : editingVendor ? "Update Vendor" : "Create Vendor"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VendorDialog;
